import React from 'react';
import { XIcon } from '@heroicons/react/solid';
import { getColorClasses, getHexColor } from './constants';

/**
 * TaskTagChip - Displays a single tag badge with an optional remove button.
 *
 * @param {Object} props
 * @param {{ id: string, name: string, color: string }} props.tag - Tag record.
 * @param {boolean} [props.showRemove=false] - Whether to render the × remove button.
 * @param {Function} [props.onRemove] - Called with the tag object when the remove button is clicked.
 * @param {'sm'|'md'} [props.size='sm'] - Controls text and padding sizes.
 * @returns {JSX.Element}
 */
const TaskTagChip = ({ tag, showRemove = false, onRemove, size = 'sm' }) => {
  const colors = getColorClasses(tag.color);
  const hexColor = getHexColor(tag.color);
  const isCustom = colors.isCustom;

  /** Tailwind padding and text-size classes based on the `size` prop. */
  const sizeClasses = size === 'sm'
    ? 'text-xs px-2 py-0.5'
    : 'text-sm px-2.5 py-1';

  // For custom colors, use inline styles
  const customBgStyle = isCustom ? { backgroundColor: `${hexColor}20` } : {};
  const customTextStyle = isCustom ? { color: hexColor } : {};

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${!isCustom ? colors.bg : ''} ${!isCustom ? colors.text : ''} ${sizeClasses} font-medium`}
      style={{ ...customBgStyle, ...customTextStyle }}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${!isCustom ? colors.dot : ''}`}
        style={isCustom ? { backgroundColor: hexColor } : {}}
      />
      <span className="truncate max-w-[150px]">{tag.name}</span>
      {showRemove && onRemove && (
        <button
          type="button"
          aria-label={`Remove ${tag.name} tag`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag);
          }}
          className="ml-0.5 -mr-1 h-4 w-4 rounded-full inline-flex items-center justify-center hover:opacity-75 focus:outline-none"
        >
          <XIcon className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
};

export default TaskTagChip;
